import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deliveryManagerAPI,
  getApiErrorMessage,
  type DeliveryBoardDto,
  type DeliveryBoardOrderDto,
  type DeliveryBoardParams,
} from '@matix/api-client';
import { wallMs } from './model';

/**
 * The Delivery Manager board, kept fresh.
 *
 * The legacy page re-read everything every THREE minutes and chimed when a new
 * order id appeared on today's board. This polls every 20 seconds (one batched
 * query, ~0.3 s on the busiest tenant probed), again the moment the tab regains
 * focus, and keeps the chime — only on today's board, only for an id higher
 * than any seen before, and never on the first load.
 */
export function useDeliveryBoard(params: DeliveryBoardParams, opts: { pollSeconds?: number; onNewOrder?: () => void } = {}) {
  const pollSeconds = opts.pollSeconds ?? 20;
  const [board, setBoard] = useState<DeliveryBoardDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState<number | null>(null);
  /** Tenant wall clock minus the browser clock, from the last response. */
  const [clockOffset, setClockOffset] = useState(0);

  const key = JSON.stringify(params);
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const onNewRef = useRef(opts.onNewOrder);
  onNewRef.current = opts.onNewOrder;

  // Newest-request-wins: a slow response for yesterday must not land on top
  // of today's board after the cashier has switched back.
  const seq = useRef(0);
  const maxSeen = useRef<{ key: string; id: number } | null>(null);

  const load = useCallback(async (quiet = false) => {
    const mine = ++seq.current;
    if (!quiet) setLoading(true);
    try {
      const res = await deliveryManagerAPI.board(paramsRef.current);
      if (mine !== seq.current) return;
      const now = wallMs(res.now);
      if (now != null) setClockOffset(now - Date.now());

      const k = JSON.stringify(paramsRef.current);
      const top = res.orders.reduce((m, o) => Math.max(m, o.id), 0);
      const isToday = res.now.slice(0, 10) === res.date;
      if (maxSeen.current && maxSeen.current.key === k && top > maxSeen.current.id && isToday) {
        onNewRef.current?.();
      }
      if (!maxSeen.current || maxSeen.current.key !== k || top > maxSeen.current.id) {
        maxSeen.current = { key: k, id: top };
      }

      setBoard(res);
      setError(null);
      setLoadedAt(Date.now());
    } catch (err) {
      if (mine !== seq.current) return;
      setError(getApiErrorMessage(err));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load(true);
    }, Math.max(10, pollSeconds) * 1000);
    const onFocus = () => void load(true);
    window.addEventListener('focus', onFocus);
    return () => {
      seq.current++;
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, pollSeconds, load]);

  /** Put a row the server just returned in place, before the next poll. */
  const patch = useCallback((order: DeliveryBoardOrderDto) => {
    setBoard((b) => (b ? { ...b, orders: b.orders.map((o) => (o.id === order.id ? order : o)) } : b));
  }, []);

  return { board, loading, error, loadedAt, clockOffset, refresh: () => void load(true), patch };
}

/** A soft two-note chime, synthesised — no audio asset to ship or cache. */
export function playChime(): void {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const note = (freq: number, at: number) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + at);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.45);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + at);
      o.stop(ctx.currentTime + at + 0.5);
    };
    note(880, 0);
    note(1318.5, 0.18);
    window.setTimeout(() => void ctx.close(), 1200);
  } catch {
    /* a till with no audio output is not an error */
  }
}
